/**
 * Configuration centralisée des widgets Brydge
 *
 * Définit les paramètres par défaut, les contraintes de dimensions
 * et les métadonnées pour chaque type de widget disponible.
 *
 * @module widgets/config
 */

/**
 * Types de widgets disponibles
 * @readonly
 * @enum {string}
 */
export const WIDGET_TYPES = {
  RESERVATIONS: 'reservations',
  GIFTVOUCHERS: 'giftvouchers',
};

/**
 * Configuration des dimensions de l'iframe selon le mode d'affichage
 * @typedef {Object} IframeDimensions
 * @property {number} minHeight - Hauteur minimale en pixels
 * @property {number|string} maxHeight - Hauteur maximale en pixels ou valeur CSS
 * @property {number} debounceDelay - Délai de debounce pour le resize en ms
 */

/**
 * Configuration d'un widget
 * @typedef {Object} WidgetConfig
 * @property {string} path - Chemin de la route
 * @property {string} title - Titre par défaut du widget
 * @property {string} buttonText - Texte du bouton d'ouverture
 * @property {string} loadingMessage - Message affiché pendant le chargement
 * @property {IframeDimensions} dimensions.fixed - Dimensions en mode fixed
 * @property {IframeDimensions} dimensions.floating - Dimensions en mode floating
 */

/**
 * Configuration complète des widgets
 * @type {Object.<string, WidgetConfig>}
 */
export const WIDGET_CONFIG = {
  [WIDGET_TYPES.RESERVATIONS]: {
    path: '/reservations',
    title: 'Réserver une table',
    titleConfirmation: 'Réservation confirmée',
    buttonText: 'Réserver une table',
    loadingMessage: 'Chargement...',
    dimensions: {
      fixed: {
        // Marge suffisante pour un calendrier à six semaines surmonté du bandeau
        // d'annonce et de celui des prochaines disponibilités : en deçà, la mesure
        // serait écrêtée et le contenu défilerait à l'intérieur de l'iframe.
        minHeight: 400,
        maxHeight: 1400,
        debounceDelay: 200,
      },
      floating: {
        minHeight: 400,
        maxHeight: 850,
        debounceDelay: 200,
      },
    },
    features: {
      requiresStripe: false,
      hasFilters: false,
      hasPagination: false,
      hasStepper: true,
    },
  },

  [WIDGET_TYPES.GIFTVOUCHERS]: {
    path: '/giftvouchers',
    title: 'Bons Cadeaux',
    buttonText: 'Acheter un bon cadeau',
    loadingMessage: 'Chargement des bons cadeaux...',
    dimensions: {
      fixed: {
        minHeight: 500,
        maxHeight: 10000,
        debounceDelay: 200,
      },
      floating: {
        minHeight: 500,
        maxHeight: '75vh',
        debounceDelay: 200,
      },
    },
    features: {
      requiresStripe: true,
      hasFilters: true,
      hasPagination: true,
      hasStepper: false,
    },
  },
};

/**
 * Récupère la configuration d'un widget par son type
 *
 * @param {string} widgetType - Type du widget (reservations, giftvouchers)
 * @return {WidgetConfig|undefined} Configuration du widget ou undefined
 *
 * @example
 * const config = getWidgetConfig('reservations');
 * console.log(config.title); // 'Réserver une table'
 */
export function getWidgetConfig(widgetType) {
  return WIDGET_CONFIG[widgetType];
}

/**
 * Récupère les dimensions de l'iframe pour un widget selon son mode d'affichage
 *
 * @param {string} widgetType - Type du widget
 * @param {string} displayMode - Mode d'affichage ('fixed' ou 'floating')
 * @return {IframeDimensions} Configuration des dimensions
 *
 * @example
 * const dims = getWidgetDimensions('reservations', 'floating');
 * // { minHeight: 400, maxHeight: 850, debounceDelay: 200 }
 */
export function getWidgetDimensions(widgetType, displayMode) {
  const config = WIDGET_CONFIG[widgetType];
  if (!config) {
    // Valeurs par défaut si le widget n'est pas configuré
    return {
      minHeight: 400,
      maxHeight: displayMode === 'floating' ? 850 : 2000,
      debounceDelay: 200,
    };
  }
  return config.dimensions[displayMode] || config.dimensions.fixed;
}

/**
 * Liste tous les types de widgets disponibles
 *
 * @return {string[]} Tableau des identifiants de widgets
 *
 * @example
 * const types = getWidgetTypes();
 * // ['reservations', 'giftvouchers']
 */
export function getWidgetTypes() {
  return Object.values(WIDGET_TYPES);
}

/**
 * Vérifie si un type de widget est valide
 *
 * @param {string} widgetType - Type à vérifier
 * @return {boolean} True si le type est valide
 *
 * @example
 * isValidWidgetType('reservations'); // true
 * isValidWidgetType('unknown'); // false
 */
export function isValidWidgetType(widgetType) {
  return Object.values(WIDGET_TYPES).includes(widgetType);
}

export default WIDGET_CONFIG;
